export type MediaErrorType =
  | 'permissionDenied'
  | 'deviceNotFound'
  | 'deviceNotReadable'
  | 'deviceOverconstrained'
  | 'aborted'
  | 'insecureContext';

export interface ClassifiedMediaError {
  errorType: MediaErrorType;
  category: string;
  domExceptionName: string;
}

const DOM_EXCEPTION_NAME_TO_TYPE: Record<string, { errorType: MediaErrorType; category: string }> = {
  NotAllowedError: { errorType: 'permissionDenied', category: 'camera_microphone_permission' },
  PermissionDeniedError: { errorType: 'permissionDenied', category: 'camera_microphone_permission' },
  NotFoundError: { errorType: 'deviceNotFound', category: 'camera_microphone_device_error' },
  NotReadableError: { errorType: 'deviceNotReadable', category: 'camera_microphone_device_error' },
  OverconstrainedError: { errorType: 'deviceOverconstrained', category: 'camera_microphone_device_error' },
  AbortError: { errorType: 'aborted', category: 'camera_microphone_device_error' },
  SecurityError: { errorType: 'insecureContext', category: 'camera_microphone_device_error' },
};

/**
 * Single source of truth for mapping a DOMException raised by getUserMedia()/Zoom's
 * startVideo() to a stable error type + Sentry category. Returns undefined when `err`
 * isn't a real DOMException instance or its name isn't one we recognize — callers
 * decide their own fallback (useZoomVideo's fallback differs by phase; useDeviceCheck's
 * doesn't), so that fallback logic isn't duplicated in here.
 */
export function classifyMediaError(err: unknown): ClassifiedMediaError | undefined {
  if (!(err instanceof DOMException)) return undefined;

  const match = DOM_EXCEPTION_NAME_TO_TYPE[err.name];
  if (!match) return undefined;

  return { ...match, domExceptionName: err.name };
}

/**
 * Safely extracts a human-readable message from a thrown value. `err instanceof
 * Error` alone isn't enough: getUserMedia()/Zoom SDK failures are almost always
 * DOMExceptions, and real browsers' DOMException does NOT extend Error (unlike
 * Node/jsdom's built-in DOMException, which does — so this gap doesn't show up
 * under test unless checked for directly). Checking `instanceof DOMException`
 * too, the same way classifyMediaError above does, catches those cases and
 * keeps their real message instead of silently downgrading to "Unknown error".
 */
export function extractErrorMessage(err: unknown): string {
  if (err instanceof Error || err instanceof DOMException) return err.message;
  return 'Unknown error';
}
